import { useLocation, useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useCallback } from 'react';
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell } from 'recharts';
import { useAuth } from '../context/AuthContext';
import { parseServerDate } from '../utils/formatDate';

const ScoreColor = s => s<=2?'var(--red)':s<=3?'var(--orange)':s<=3.8?'var(--yellow)':s<=4.5?'var(--green)':'var(--cyan)';

// Roadmap items come in two shapes: legacy plain strings (free-tier template,
// or older cached AI roadmaps generated before this format existed) and the
// newer {title, detail, owner, metric} objects. These helpers normalise both
// so the UI always has a short title to show and never crashes on either shape.
const roadmapTitle  = a => (typeof a === 'string' ? a : (a?.title || 'Untitled action'));
const roadmapDetail = a => (typeof a === 'string' ? '' : (a?.detail || ''));
const roadmapOwner  = a => (typeof a === 'string' ? '' : (a?.owner  || ''));
const roadmapMetric = a => (typeof a === 'string' ? '' : (a?.metric || ''));

const RadarTick = ({ x, y, textAnchor, value, index }) => {
  // Offset labels slightly outward for better readability
  const dx = textAnchor === 'end' ? -4 : textAnchor === 'start' ? 4 : 0;
  return (
    <text x={x + dx} y={y} textAnchor={textAnchor}
      fill="var(--text2)" fontSize={10}
      fontFamily="'JetBrains Mono',monospace"
      fontWeight={500}>
      {value}
    </text>
  );
};

function InsightCard({ ins }) {
  return (
    <div className={`insight insight-${ins.type}`}>
      <div className="insight-icon">{ins.icon}</div>
      <div><div className="insight-title">{ins.title}</div><div className="insight-body">{ins.body}</div></div>
    </div>
  );
}

function CompareView({ compare, navigate }) {
  if (!compare) return null;
  return (
    <div className="card fade-in" style={{ marginBottom: 24, borderColor: 'var(--blue-border)' }}>
      <div className="card-title">Round Comparison — {compare.round1?.label} vs {compare.round2?.label}</div>
      <div className="g2" style={{ marginBottom: 20 }}>
        {[{ label: compare.round1?.label, data: compare.before }, { label: compare.round2?.label, data: compare.after }].map((r, i) => (
          <div key={i} style={{ padding: '14px', background: 'var(--bg3)', borderRadius: 'var(--r)' }}>
            <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--ff-m)', marginBottom: 6 }}>{r.label}</div>
            <div style={{ fontFamily: 'var(--ff-m)', fontSize: 32, color: ScoreColor(r.data.org_score) }}>{r.data.org_score}</div>
            <div style={{ fontSize: 11, color: 'var(--text2)' }}>{r.data.maturity?.label} · {r.data.respondents} respondents</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, padding: '10px 14px', background: compare.org_delta > 0 ? 'rgba(34,197,94,0.08)' : compare.org_delta < 0 ? 'rgba(239,68,68,0.08)' : 'var(--bg-elevated)', borderRadius: 'var(--r)' }}>
        <span style={{ fontSize: 24 }}>{compare.org_delta > 0 ? '📈' : compare.org_delta < 0 ? '📉' : '➡️'}</span>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Overall change: <span className={compare.org_delta > 0 ? 'delta-positive' : compare.org_delta < 0 ? 'delta-negative' : 'delta-neutral'}>{compare.org_delta > 0 ? '+' : ''}{compare.org_delta}</span></div>
          <div style={{ fontSize: 12, color: 'var(--text2)' }}>{compare.org_delta > 0 ? 'Organization improved' : compare.org_delta < 0 ? 'Scores declined — needs attention' : 'No change detected'}</div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {Object.entries(compare.dimension_changes || {}).map(([key, d]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontSize: 13, flex: 1 }}>{d.label}</div>
            <span style={{ fontFamily: 'var(--ff-m)', fontSize: 12, color: 'var(--text3)' }}>{d.before} → {d.after}</span>
            <span className={d.delta > 0 ? 'delta-positive' : d.delta < 0 ? 'delta-negative' : 'delta-neutral'}>{d.delta > 0 ? '+' : ''}{d.delta}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Report({ showToast, user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const pageData = location.state || {};
  const { authFetch } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [selCompany, setSelCompany] = useState(pageData?.companyId || '');
  const [rounds, setRounds] = useState([]);
  const [selRound, setSelRound] = useState('');
  const [compareRound, setCompareRound] = useState('');
  const [report, setReport] = useState(null);
  const [compare, setCompare] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState('');
  const [roadmapProgress, setRoadmapProgress] = useState({}); // { action_text: { id, status } }
  const [expandedRoadmapItems, setExpandedRoadmapItems] = useState({}); // { 'phaseKey-index': bool } — collapsible detail/owner/metric per item
  const [aiRoadmap, setAiRoadmap]     = useState(null);   // AI-generated roadmap (Pro only)
  const [aiRoadmapLoading, setAiRoadmapLoading] = useState(false);
  const [aiRoadmapError,   setAiRoadmapError]   = useState('');

  useEffect(() => { authFetch('/api/companies').then(r => r.json()).then(d => setCompanies(Array.isArray(d) ? d : [])); }, [authFetch]);
  useEffect(() => { if (pageData?.companyId) setSelCompany(pageData.companyId); }, [pageData]);

  useEffect(() => {
    if (!selCompany) return;
    authFetch(`/api/assessments/rounds/${selCompany}`).then(r => r.json()).then(d => {
      if (Array.isArray(d)) { setRounds(d); if (d.length > 0) setSelRound(d[d.length - 1].id); }
    });
    // Load existing roadmap progress
    loadRoadmapProgress(selCompany);
  }, [selCompany, authFetch]);

  const loadRoadmapProgress = async (cid) => {
    try {
      const res = await authFetch(`/api/roadmap/${cid}`);
      if (res.ok) {
        const items = await res.json();
        const map = {};
        items.forEach(it => { map[it.action_text] = it; });
        setRoadmapProgress(map);
      }
    } catch {}
  };

  useEffect(() => { if (selCompany && selRound) loadReport(); }, [selCompany, selRound]);

  const loadReport = async () => {
    setLoading(true); setError(''); setReport(null); setCompare(null);
    setAiRoadmap(null); setAiRoadmapError('');
    const url = selRound ? `/api/reports/${selCompany}?round_id=${selRound}` : `/api/reports/${selCompany}`;
    const res = await authFetch(url);
    const data = await res.json();
    if (res.ok) {
      setReport(data);
      // Auto-seed roadmap items for tracking
      if (data.roadmap && !data.roadmap.locked) {
        // action_text must always be a plain string for the DB column — items
        // can be legacy plain strings (free-tier template) or the new
        // {title,detail,owner,metric} objects (AI-generated); .title||a
        // handles both without ever binding a raw object to SQLite.
        const items = [
          ...(data.roadmap.day30 || []).map(a => ({ action_text: a.title || a, phase: 'day30' })),
          ...(data.roadmap.day60 || []).map(a => ({ action_text: a.title || a, phase: 'day60' })),
          ...(data.roadmap.day90 || []).map(a => ({ action_text: a.title || a, phase: 'day90' })),
        ];
        if (items.length) {
          authFetch(`/api/roadmap/${data.company_id}/seed`, { method: 'POST', body: JSON.stringify({ items }) })
            .then(() => loadRoadmapProgress(data.company_id)).catch(() => {});
        }
      }
      // For Pro/Enterprise: fetch AI roadmap asynchronously after report loads
      if (data.is_pro) {
        fetchAIRoadmap(data.company_id, selRound, false);
      }
    } else setError(data.error || 'Failed to load report');
    setLoading(false);
  };

  // Fetch (or generate) the AI roadmap for Pro users.
  // force=true bypasses the cache and triggers a fresh AI generation.
  const fetchAIRoadmap = async (companyId, roundId, force = false) => {
    setAiRoadmapLoading(true);
    setAiRoadmapError('');
    try {
      const res = await authFetch(`/api/reports/${companyId}/ai-roadmap`, {
        method: 'POST',
        body: JSON.stringify({ round_id: roundId || '', force }),
      });
      const data = await res.json();
      if (res.ok) {
        setAiRoadmap(data);
        // Re-seed progress tracking with AI items (they differ from static ones)
        if (data.day30 || data.day60 || data.day90) {
          const items = [
            ...(data.day30 || []).map(a => ({ action_text: a.title || a, phase: 'day30' })),
            ...(data.day60 || []).map(a => ({ action_text: a.title || a, phase: 'day60' })),
            ...(data.day90 || []).map(a => ({ action_text: a.title || a, phase: 'day90' })),
          ];
          authFetch(`/api/roadmap/${companyId}/seed`, { method:'POST', body: JSON.stringify({ items }) })
            .then(() => loadRoadmapProgress(companyId)).catch(() => {});
        }
      } else {
        setAiRoadmapError(data.error || 'AI roadmap unavailable');
      }
    } catch {
      setAiRoadmapError('Network error — using standard roadmap');
    }
    setAiRoadmapLoading(false);
  };

  const markRoadmapItem = async (actionText, status) => {
    const item = roadmapProgress[actionText];
    if (!item) return;
    try {
      const res = await authFetch(`/api/roadmap/${selCompany}/${item.id}`, { method: 'PUT', body: JSON.stringify({ status }) });
      if (res.ok) {
        const updated = await res.json();
        setRoadmapProgress(prev => ({ ...prev, [actionText]: updated }));
        showToast(status === 'done' ? '✓ Marked as done' : 'Status updated');
      }
    } catch {}
  };

  const loadComparison = async () => {
    if (!compareRound || !selRound) return;
    try {
      const res = await authFetch(`/api/reports/${selCompany}/compare?r1=${compareRound}&r2=${selRound}`);
      if (res.ok) {
        const data = await res.json();
        setCompare(data);
      } else {
        showToast('Comparison failed — ensure both rounds have data', 'error');
      }
    } catch {
      showToast('Comparison failed', 'error');
    }
  };

  const exportPDF = () => {
    if (!report) return;
    const content = document.getElementById('report-content');
    if (!content) return;
    const win = window.open('', '_blank');
    win.document.write(`<html><head><title>Org DNA Report - ${report.company_name}</title>
    <style>
      body{font-family:Arial,sans-serif;padding:40px;color:#111;background:#fff}
      h1{font-size:28px;margin-bottom:4px}h2{font-size:18px;margin:24px 0 12px;color:#0f172a;border-bottom:2px solid #e2e8f0;padding-bottom:8px}
      .score{font-size:48px;font-weight:700;color:#0891b2;line-height:1}.badge{display:inline-block;padding:4px 12px;border-radius:20px;font-size:12px;font-weight:600}
      table{width:100%;border-collapse:collapse;margin-top:12px}th,td{padding:10px;border:1px solid #e2e8f0;font-size:13px;text-align:left}th{background:#f8fafc;font-weight:600}
      .insight{padding:12px 16px;margin-bottom:8px;border-left:3px solid #0891b2;background:#f0f9ff;border-radius:4px}
      .roadmap-col{flex:1;padding:12px;border:1px solid #e2e8f0;border-radius:8px}.roadmap-wrap{display:flex;gap:12px}
      @media print{body{padding:20px}.no-print{display:none}}
    </style></head><body>
    <div style="border-bottom:2px solid #0891b2;padding-bottom:20px;margin-bottom:28px">
      <h1>Org DNA™ Diagnostic Report</h1>
      <div style="color:#64748b;font-size:14px">${report.company_name} · ${parseServerDate(report.generated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })} · ${report.respondent_count} respondents</div>
    </div>
    <div style="display:flex;align-items:center;gap:32px;margin-bottom:28px">
      <div><div class="score">${report.org_score}</div><div style="color:#64748b;font-size:12px;margin-top:4px">OVERALL ORG DNA SCORE / 5</div></div>
      <div><span class="badge" style="background:${report.maturity.color}22;color:${report.maturity.color};border:1px solid ${report.maturity.color}">${report.maturity.label}</span><div style="margin-top:6px;color:#64748b;font-size:13px">${report.maturity.desc}</div></div>
    </div>
    ${report.narrative ? `<h2>Executive Summary</h2><p style="color:#374151;line-height:1.7;font-size:14px">${report.narrative}</p>` : ''}
    <h2>Dimension Scores</h2>
    <table><thead><tr><th>Dimension</th><th>Score</th><th>Variance</th><th>Maturity</th></tr></thead><tbody>
    ${(report.dimensions || []).filter(d => !d.locked).map(d => `<tr><td>${d.label}</td><td style="font-weight:700;color:${ScoreColor(d.score)}">${d.score}</td><td>${d.variance}</td><td>${d.maturity?.label || ''}</td></tr>`).join('')}
    </tbody></table>
    ${report.insights?.length ? `<h2>Key Insights</h2>${report.insights.map(i => `<div class="insight"><strong>${i.title}</strong><br><span style="font-size:13px;color:#374151">${i.body}</span></div>`).join('')}` : ''}
    ${report.roadmap && !report.roadmap.locked ? `<h2>30–60–90 Day Roadmap</h2><div class="roadmap-wrap">
      <div class="roadmap-col"><strong style="color:#ef4444">0–30 Days (Stabilize)</strong><ul style="margin-top:8px;padding-left:16px">${(report.roadmap.day30 || []).map(a => `<li style="font-size:13px;margin-bottom:4px"><strong>${a.title || a}</strong>${a.detail ? `<br><span style="font-size:11px;color:#6b7280">${a.detail}</span>` : ''}</li>`).join('')}</ul></div>
      <div class="roadmap-col"><strong style="color:#f59e0b">30–60 Days (Structure)</strong><ul style="margin-top:8px;padding-left:16px">${(report.roadmap.day60 || []).map(a => `<li style="font-size:13px;margin-bottom:4px"><strong>${a.title || a}</strong>${a.detail ? `<br><span style="font-size:11px;color:#6b7280">${a.detail}</span>` : ''}</li>`).join('')}</ul></div>
      <div class="roadmap-col"><strong style="color:#22c55e">60–90 Days (Optimize)</strong><ul style="margin-top:8px;padding-left:16px">${(report.roadmap.day90 || []).map(a => `<li style="font-size:13px;margin-bottom:4px"><strong>${a.title || a}</strong>${a.detail ? `<br><span style="font-size:11px;color:#6b7280">${a.detail}</span>` : ''}</li>`).join('')}</ul></div>
    </div>` : ''}
    <div style="margin-top:40px;padding-top:20px;border-top:1px solid #e2e8f0;color:#94a3b8;font-size:12px">
      Generated by Enpower Org DNA™ Platform · Confidential
    </div>
    <script>window.print();<\/script></body></html>`);
    win.document.close();
  };

  const exportCSV = () => {
    if (!report?.dimensions) return;
    const rows = [['Dimension', 'Score', 'Variance', 'Maturity', 'Priority']];
    (report.dimensions || []).filter(d => !d.locked).forEach(d => {
      const p = report.priorities?.find(p => p.dim === d.key);
      rows.push([d.label, d.score, d.variance, d.maturity?.label, p?.priority || '']);
    });
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${report.company_name}-OrgDNA-Report.csv`; a.click();
  };

  const radarData = report ? (report.dimensions || []).filter(d => !d.locked && d.score != null).map(d => ({ subject: d.code, score: d.score, fullMark: 5 })) : [];
  const deptData = report ? Object.entries(report.dept_scores || {}).map(([k, v]) => ({ name: k, score: Math.round(v * 10) / 10 })).sort((a, b) => b.score - a.score) : [];
  const currentCompany = companies.find(c => c.id === selCompany);

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Diagnostic Report</div>
        <div className="page-sub">Full organizational health analysis — scores, insights, and action roadmap</div>
      </div>

      {/* Selectors */}
      <div className="card" style={{ marginBottom: 24, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <label className="form-label">Company</label>
          <select className="form-select" value={selCompany} onChange={e => { setSelCompany(e.target.value); setSelRound(''); setReport(null); }}>
            <option value="">Select company...</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name} ({c.response_count || 0} responses)</option>)}
          </select>
        </div>
        {rounds.length > 0 && (
          <div>
            <label className="form-label">Audit Round</label>
            <div className="round-selector">
              {rounds.map(r => (
                <button key={r.id} className={`round-btn ${selRound === r.id ? 'active' : ''} ${r.status === 'collecting' ? 'collecting' : ''}`}
                  onClick={() => { setSelRound(r.id); setCompare(null); setCompareRound(''); }}>
                  {r.label} ({r.response_count || 0})
                </button>
              ))}
            </div>
          </div>
        )}
        {selCompany && <button className="btn btn-ghost btn-sm" onClick={() => navigate('/assessment', { state: { companyId: selCompany, companyName: currentCompany?.name } })}>+ Add Responses</button>}
      </div>

      {/* Round comparison */}
      {rounds.length > 1 && report?.is_pro && (
        <div className="card" style={{ marginBottom: 20, padding: '14px 18px', background: 'rgba(124,58,237,0.04)', borderColor: 'rgba(124,58,237,0.2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>📊 Compare with:</div>
            <select className="form-select" style={{ maxWidth: 200 }} value={compareRound} onChange={e => setCompareRound(e.target.value)}>
              <option value="">Select a previous round...</option>
              {rounds.filter(r => r.id !== selRound).map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
            <button className="btn btn-ghost btn-sm" disabled={!compareRound} onClick={loadComparison}>Compare →</button>
          </div>
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', padding: 60 }}><div className="loader" style={{ margin: '0 auto' }}></div></div>}
      {error && !loading && (
        <div className="card" style={{ textAlign: 'center', padding: 48, borderColor: 'var(--red)' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
          <div style={{ color: 'var(--red)', marginBottom: 12 }}>{error}</div>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/companies')}>Go to Companies</button>
        </div>
      )}

      {compare && <CompareView compare={compare} navigate={navigate} />}

      {report && !loading && (
        <div className="fade-in" id="report-content">
          {/* Upgrade banner */}
          {!report.is_pro && (
            <div style={{ background: 'linear-gradient(135deg,var(--purple-soft),var(--blue-soft))', border: '1px solid var(--blue-border)', borderRadius: 'var(--r)', padding: '16px 20px', marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 3 }}>🔓 Unlock Full Report</div>
                <div style={{ fontSize: 12, color: 'var(--text2)' }}>You see a preview. Pro unlocks all {report.dimensions?.length} dimensions, insights, roadmap, AI, comparison, and PDF export.</div>
              </div>
              <button className="btn btn-primary" onClick={() => navigate('/billing')}>Upgrade to Pro — ₹6,999/mo</button>
            </div>
          )}

          {/* Export bar (Pro only) */}
          {report.is_pro && (
            <div className="export-bar">
              <span style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 500 }}>Export:</span>
              <button className="export-btn" onClick={exportPDF}>📄 PDF Report</button>
              <button className="export-btn" onClick={exportCSV}>📊 CSV Data</button>
              <button className="export-btn" onClick={() => window.print()}>🖨️ Print</button>
              <button className="export-btn" onClick={() => {
                const token = localStorage.getItem('token');
                const url = `/api/export/${report.company_id}/pptx${selRound ? '?round_id=' + selRound : ''}`;
                fetch(url, { headers: { Authorization: 'Bearer ' + token } })
                  .then(r => { if (!r.ok) return r.json().then(d => { throw new Error(d.error); }); return r.blob(); })
                  .then(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${report.company_name}_OrgDNA.pptx`; a.click(); })
                  .catch(e => showToast(e.message, 'error'));
              }}>📊 PPTX Slides</button>
            </div>
          )}

          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontFamily: 'var(--ff-d)', fontSize: 20, fontWeight: 800 }}>{report.company_name}</div>
              <div style={{ color: 'var(--text2)', fontSize: 12, marginTop: 3 }}>
                {report.respondent_count} respondent{report.respondent_count !== 1 ? 's' : ''} · {parseServerDate(report.generated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                {report.round && <span> · {report.round.label}</span>}
              </div>
            </div>
          </div>

          {/* Response Consistency Banner */}
          {report.consistency?.has_concerns && (
            <div style={{
              marginBottom: 16, padding: '14px 18px',
              borderRadius: 'var(--r-md)',
              background: report.consistency.severity === 'high' ? 'var(--red-soft)' : 'var(--amber-soft)',
              border: `1px solid ${report.consistency.severity === 'high' ? 'var(--red-border)' : 'var(--amber-border)'}`,
              display: 'flex', gap: 14, alignItems: 'flex-start',
            }}>
              <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>
                {report.consistency.severity === 'high' ? '⚠️' : '🔍'}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 4, color: report.consistency.severity === 'high' ? 'var(--red)' : 'var(--amber)' }}>
                  Response Consistency Alert — {report.consistency.severity === 'high' ? 'High' : 'Low'} Concern
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6, marginBottom: 8 }}>
                  {report.consistency.flagged} of {report.consistency.total} respondents ({report.consistency.flagged_pct}%) showed inconsistent or potentially random answer patterns.
                  Scores in this report are computed using the <strong>{report.consistency.clean} clean responses</strong> only.
                </div>
                {report.consistency.flagged_respondents?.length > 0 && (
                  <details>
                    <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--text-3)', fontWeight: 600, letterSpacing: 0.3 }}>
                      View flagged respondents ({report.consistency.flagged_respondents.length})
                    </summary>
                    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 7 }}>
                      {report.consistency.flagged_respondents.map((r, i) => (
                        <div key={i} style={{ padding: '8px 12px', background: 'rgba(0,0,0,0.04)', borderRadius: 7, fontSize: 12 }}>
                          <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>{r.name} {r.department ? `· ${r.department}` : ''}</div>
                          <div style={{ color: 'var(--text-3)' }}>{r.reasons.join(' · ')}</div>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            </div>
          )}

          {/* Quick summary banner — top of report */}
          <div style={{ marginBottom:20, padding:'16px 20px', borderRadius:'var(--r-lg)', background:'var(--blue-soft)', border:'1px solid var(--blue-border)', display:'flex', alignItems:'center', gap:20, flexWrap:'wrap' }}>
            <div style={{ flexShrink:0, textAlign:'center' }}>
              <div style={{ fontFamily:'var(--ff-d)', fontSize:48, lineHeight:1, color:ScoreColor(report.org_score) }}>{report.org_score}</div>
              <div style={{ fontSize:10, color:'var(--text3)', fontFamily:'var(--ff-m)', letterSpacing:1.5, marginTop:3 }}>/ 5.0</div>
            </div>
            <div style={{ width:1, height:52, background:'var(--border)', flexShrink:0 }}/>
            <div style={{ flex:1, minWidth:180 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:5 }}>
                <span className="badge" style={{ color:report.maturity.color, borderColor:report.maturity.color, background:`${report.maturity.color}18`, fontSize:10 }}>● {report.maturity.label}</span>
                <span style={{ fontSize:12, color:'var(--text2)' }}>{report.company_name}</span>
                {report.round && <span className="tag" style={{ fontSize:10 }}>{report.round.label}</span>}
              </div>
              <div style={{ fontSize:13, color:'var(--text2)', lineHeight:1.6 }}>{report.maturity.desc}</div>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:6, flexShrink:0 }}>
              {[
                { label:'Respondents', value:report.respondent_count, color:'var(--text)' },
                { label:'Critical issues', value:report.insights?.filter(i=>i.type==='critical').length||0, color:'var(--rose)' },
                { label:'Dimensions', value:report.dimensions?.filter(d=>!d.locked).length||0, color:'var(--blue)' },
              ].map(s => (
                <div key={s.label} style={{ display:'flex', justifyContent:'space-between', gap:16, alignItems:'center' }}>
                  <span style={{ fontSize:11, color:'var(--text3)', fontFamily:'var(--ff-m)', textTransform:'uppercase', letterSpacing:0.8 }}>{s.label}</span>
                  <span style={{ fontSize:13, fontWeight:700, color:s.color }}>{s.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Top stats */}
          <div className="g4" style={{ marginBottom: 24 }}>
            <div className="card" style={{ gridColumn: 'span 2', background: 'var(--blue-soft)', borderColor: 'var(--blue-border)' }}>
              <div className="card-title">Overall Org DNA Score</div>
              <div style={{ fontFamily: 'var(--ff-m)', fontSize: 52, fontWeight: 500, color: ScoreColor(report.org_score), lineHeight: 1 }}>{report.org_score}</div>
              <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4, fontFamily: 'var(--ff-m)', letterSpacing: 1 }}>OUT OF 5.0</div>
              <div style={{ marginTop: 10 }}>
                <span className="badge" style={{ color: report.maturity.color, borderColor: report.maturity.color, background: `${report.maturity.color}18`, fontSize: 10 }}>● {report.maturity.label}</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 7 }}>{report.maturity.desc}</div>
            </div>
            <div className="card"><div className="card-title">Respondents</div><div className="stat-num" style={{ color: 'var(--gold-2)' }}>{report.respondent_count}</div></div>
            <div className="card"><div className="card-title">Issues Found</div><div className="stat-num" style={{ color: 'var(--red)' }}>{report.insights?.filter(i => i.type === 'critical').length || 0}</div><div className="stat-label" style={{ fontSize: 10 }}>Critical</div></div>
          </div>

          {/* AI Narrative */}
          {report.narrative && (
            <div className="card" style={{ marginBottom: 24, borderColor: 'var(--blue-border)', background: 'var(--blue-soft)' }}>
              <div className="card-title">🤖 AI Executive Summary</div>
              <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>{report.narrative}</div>
            </div>
          )}

          {/* Document context */}
          {report.doc_context && (
            <div className="card" style={{ marginBottom: 24, borderColor: 'rgba(245,166,35,0.2)', background: 'rgba(245,166,35,0.03)' }}>
              <div className="card-title">📁 Document Intelligence Active</div>
              <div style={{ fontSize: 12, color: 'var(--text2)' }}>Uploaded documents have been analyzed and cross-referenced with your survey scores in the AI narrative above.</div>
            </div>
          )}

          {/* Radar + Bars */}
          <div className="g2" style={{ marginBottom: 24 }}>
            <div className="card" style={{ position: 'relative' }}>
              <div className="card-title">Org DNA Radar</div>
              {!report.is_pro && radarData.length < 5 && (
                <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: '50%', background: 'linear-gradient(90deg,transparent,rgba(8,12,20,0.95))', borderRadius: '0 var(--r-lg) var(--r-lg) 0', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5 }}>
                  <div style={{ textAlign: 'center', paddingLeft: 20 }}>
                    <div style={{ fontSize: 24, marginBottom: 6 }}>🔒</div>
                    <button className="btn btn-primary btn-sm" onClick={() => navigate('/billing')}>Upgrade</button>
                  </div>
                </div>
              )}
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData} margin={{ top: 16, right: 40, bottom: 16, left: 40 }} outerRadius="72%">
                    <PolarGrid stroke="var(--border)" strokeOpacity={0.6} />
                    <PolarAngleAxis dataKey="subject" tick={<RadarTick />} tickLine={false} />
                    <Radar
                      dataKey="score"
                      stroke="var(--accent)"
                      fill="var(--accent)"
                      fillOpacity={0.18}
                      strokeWidth={2.5}
                      dot={{ fill: 'var(--accent)', r: 4, strokeWidth: 2, stroke: 'var(--bg-surface)' }}
                      activeDot={{ r: 6, fill: 'var(--accent)' }}
                    />
                    <Tooltip
                      formatter={(v) => [v.toFixed(2) + ' / 5', 'Score']}
                      contentStyle={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}
                      labelStyle={{ fontWeight: 700, color: 'var(--text)' }}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="card">
              <div className="card-title">Dimension Scores ({(report.dimensions || []).filter(d => !d.locked).length} active)</div>
              {(report.dimensions || []).map((d, i) => (
                <div key={d.key} style={{ marginBottom: 10, position: 'relative' }}>
                  {d.locked ? (
                    <div style={{ filter: 'blur(4px)', userSelect: 'none', marginBottom: 4 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}><span style={{ fontSize: 12 }}>{d.label}</span><span className="mono" style={{ fontSize: 12 }}>?.?</span></div>
                      <div className="bar-track"><div className="bar-fill" style={{ width: '55%', background: 'var(--border2)' }}></div></div>
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ fontSize: 12, fontWeight: 500 }}>{d.label}</span>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <span className="mono" style={{ fontSize: 10, color: 'var(--text3)' }}>±{d.variance}</span>
                          <span className="mono" style={{ fontSize: 13, fontWeight: 500, color: ScoreColor(d.score) }}>{d.score}</span>
                        </div>
                      </div>
                      <div className="bar-track"><div className="bar-fill" style={{ width: `${(d.score / 5) * 100}%`, background: d.color || 'var(--accent)' }}></div></div>
                    </>
                  )}
                  {d.locked && i === 2 && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <button className="btn btn-primary btn-sm" onClick={() => navigate('/billing')}>🔒 Unlock All Dims</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Priority Heatmap */}
          {report.priorities?.length > 0 && (
            <>
              <div className="sec-header"><div className="sec-title">Priority Heatmap</div><div className="sec-line"></div></div>
              <div className="card" style={{ marginBottom: 24, padding: 0, overflow: 'hidden' }}>
                <table className="table">
                  <thead><tr><th>Dimension</th><th>Score</th><th>Variance</th><th>Alignment</th><th>Priority</th></tr></thead>
                  <tbody>
                    {report.priorities.map(p => (
                      <tr key={p.dim}>
                        <td style={{ fontWeight: 600, color: 'var(--text)' }}>{p.label}</td>
                        <td><span style={{ fontFamily: 'var(--ff-m)', color: ScoreColor(p.score), fontWeight: 600 }}>{p.score}</span><span style={{ color: 'var(--text3)', fontSize: 11 }}>/5</span></td>
                        <td><span className="mono">{p.variance}</span></td>
                        <td><span className={`tag priority-${p.variance < 0.5 ? 'strong' : p.variance < 1 ? 'healthy' : 'critical'}`} style={{ fontSize: 10 }}>{p.variance < 0.5 ? 'Aligned' : p.variance < 1 ? 'Some gaps' : 'Misaligned'}</span></td>
                        <td><span className={`tag priority-${p.priority}`} style={{ fontSize: 10, textTransform: 'uppercase' }}>{p.priority}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Insights */}
          <div className="sec-header">
            <div className="sec-title">Key Insights</div>
            <div className="sec-line"></div>
            {report.insights_locked_count > 0 && <span className="tag">{report.insights_locked_count} more in Pro</span>}
          </div>
          <div style={{ marginBottom: 24 }}>
            {report.insights?.map((ins, i) => <InsightCard key={i} ins={ins} />)}
            {!report.is_pro && report.insights_locked_count > 0 && (
              <div className="insight insight-info" style={{ cursor: 'pointer' }} onClick={() => navigate('/billing')}>
                <div className="insight-icon">🔒</div>
                <div><div className="insight-title">{report.insights_locked_count} more insights locked</div><div className="insight-body">Upgrade to Pro to see all pattern detections.</div></div>
              </div>
            )}
          </div>

          {/* Dept breakdown */}
          {deptData.length > 1 && (
            <>
              <div className="sec-header"><div className="sec-title">Department Breakdown</div><div className="sec-line"></div></div>
              <div className="card" style={{ marginBottom: 24 }}>
                {deptData.map(d => (
                  <div key={d.name} className="heat-row">
                    <div className="heat-dept">{d.name}</div>
                    <div className="heat-bar-wrap"><div className="heat-bar" style={{ width: `${(d.score / 5) * 100}%`, background: ScoreColor(d.score) }}></div></div>
                    <div className="heat-score" style={{ color: ScoreColor(d.score) }}>{d.score}</div>
                    <span className={`tag priority-${d.score < 3 ? 'critical' : d.score < 3.5 ? 'weak' : d.score < 4 ? 'healthy' : 'strong'}`} style={{ fontSize: 10, width: 54, textAlign: 'center' }}>{d.score < 3 ? 'WEAK' : d.score < 3.5 ? 'WATCH' : d.score < 4 ? 'OK' : 'STRONG'}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Roadmap */}
          <div className="sec-header">
            <div className="sec-title">30–60–90 Day Roadmap</div>
            <div className="sec-line"></div>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              {/* Progress bar — shows against whichever roadmap is active */}
              {!report.roadmap?.locked && (() => {
                const activeRoadmap = aiRoadmap || report.roadmap;
                const total = [...(activeRoadmap?.day30||[]),...(activeRoadmap?.day60||[]),...(activeRoadmap?.day90||[])].length;
                const done  = Object.values(roadmapProgress).filter(i=>i.status==='done').length;
                return total > 0 ? (
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:100, height:6, background:'var(--bg-subtle)', borderRadius:3, overflow:'hidden' }}>
                      <div style={{ height:'100%', width:`${Math.round((done/total)*100)}%`, background:'var(--green)', borderRadius:3, transition:'width 0.4s' }} />
                    </div>
                    <span style={{ fontSize:11, color:'var(--text3)', fontFamily:'var(--ff-m)' }}>{done}/{total} done</span>
                  </div>
                ) : null;
              })()}
              {/* Regenerate button — only for Pro users when AI roadmap is shown */}
              {report.is_pro && aiRoadmap && !aiRoadmapLoading && (
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ fontSize:11, padding:'3px 10px' }}
                  title="Regenerate AI roadmap (use when new responses have been added)"
                  onClick={() => fetchAIRoadmap(report.company_id, selRound, true)}
                >
                  ↻ Regenerate
                </button>
              )}
            </div>
          </div>

          {report.roadmap?.locked ? (
            /* Free tier — locked teaser */
            <div className="card" style={{ textAlign: 'center', padding: 36, borderColor: 'var(--blue-border)', marginBottom: 24 }}>
              <div style={{ fontSize: 28, marginBottom: 10 }}>🔒</div>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Full roadmap in Pro</div>
              <div style={{ fontSize:13, color:'var(--text3)', marginBottom:16 }}>
                Pro generates an AI-powered 30/60/90 day roadmap tailored to your specific scores,
                lowest-rated questions, and employee feedback — not a generic checklist.
              </div>
              <button className="btn btn-primary" onClick={() => navigate('/billing')}>Upgrade to Pro</button>
            </div>
          ) : (
            <>
              {/* AI roadmap loading shimmer */}
              {aiRoadmapLoading && (
                <div className="card" style={{ marginBottom:16, padding:20 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
                    <div style={{ width:20, height:20, borderRadius:'50%', border:'2px solid var(--blue)', borderTopColor:'transparent', animation:'spin 0.8s linear infinite' }} />
                    <span style={{ fontSize:13, color:'var(--text2)', fontWeight:500 }}>
                      Enpower AI is analysing {report.respondent_count} responses and generating your personalised roadmap…
                    </span>
                  </div>
                  {/* Shimmer placeholders */}
                  <div className="g3" style={{ gap:12 }}>
                    {['var(--red)','var(--gold)','var(--green)'].map((col,ci) => (
                      <div key={ci} style={{ border:'1px solid var(--border)', borderTop:`3px solid ${col}`, borderRadius:10, padding:14 }}>
                        <div style={{ height:12, background:'var(--bg-subtle)', borderRadius:6, width:'60%', marginBottom:10, animation:'pulse 1.5s ease-in-out infinite' }} />
                        {[1,2,3,4].map(i => (
                          <div key={i} style={{ height:10, background:'var(--bg-subtle)', borderRadius:5, marginBottom:8, width:`${70+Math.random()*25}%`, animation:`pulse 1.5s ease-in-out ${i*0.15}s infinite` }} />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI roadmap error — silent fallback to static */}
              {aiRoadmapError && !aiRoadmapLoading && (
                <div style={{ fontSize:12, color:'var(--text-4)', marginBottom:10, padding:'6px 12px', background:'var(--bg-subtle)', borderRadius:6 }}>
                  ⚠ {aiRoadmapError} — showing standard roadmap below
                  <button className="btn btn-ghost btn-sm" style={{ fontSize:11, marginLeft:10, padding:'2px 8px' }} onClick={() => fetchAIRoadmap(report.company_id, selRound, false)}>Retry</button>
                </div>
              )}

              {/* Roadmap columns — show AI version if available, static otherwise */}
              {!aiRoadmapLoading && (() => {
                const activeRoadmap = aiRoadmap || report.roadmap;
                const isAI = !!aiRoadmap;
                return (
                  <>
                    {/* AI badge — shown when displaying AI-generated roadmap */}
                    {isAI && (
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
                        <span style={{
                          display:'inline-flex', alignItems:'center', gap:5,
                          fontSize:11, fontWeight:600, letterSpacing:'0.04em',
                          color:'var(--accent)', background:'var(--blue-soft)',
                          border:'1px solid var(--blue-border)', borderRadius:6, padding:'3px 10px',
                        }}>
                          ✦ AI-Generated · Tailored to your {report.respondent_count} responses
                        </span>
                        {aiRoadmap.cached && (
                          <span style={{ fontSize:11, color:'var(--text-4)' }}>
                            · Generated {parseServerDate(aiRoadmap.generated_at).toLocaleDateString('en-IN', { day:'numeric', month:'short' })}
                          </span>
                        )}
                      </div>
                    )}

                    <div className="g3" style={{ marginBottom: 24 }}>
                      {[
                        { phaseKey: 'day30', phase: '0–30 Days', label: 'Stabilize', actions: activeRoadmap?.day30, color: 'var(--red)' },
                        { phaseKey: 'day60', phase: '30–60 Days', label: 'Structure', actions: activeRoadmap?.day60, color: 'var(--gold)' },
                        { phaseKey: 'day90', phase: '60–90 Days', label: 'Optimize', actions: activeRoadmap?.day90, color: 'var(--green)' },
                      ].map(r => {
                        const phaseItems = (r.actions || []);
                        const phaseDone = phaseItems.filter(a => roadmapProgress[roadmapTitle(a)]?.status === 'done').length;
                        return (
                          <div key={r.phase} className="card" style={{ borderTop: `3px solid ${r.color}` }}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
                              <div>
                                <div className="mono" style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 2 }}>{r.phase}</div>
                                <div style={{ fontFamily: 'var(--ff-d)', fontSize: 16, fontWeight: 800, color: r.color }}>{r.label}</div>
                              </div>
                              {phaseItems.length > 0 && (
                                <span style={{ fontSize:11, fontFamily:'var(--ff-m)', color: phaseDone===phaseItems.length ? 'var(--green)' : 'var(--text3)' }}>
                                  {phaseDone===phaseItems.length ? '✓ Complete' : `${phaseDone}/${phaseItems.length}`}
                                </span>
                              )}
                            </div>
                            {phaseItems.map((a, i) => {
                              const title  = roadmapTitle(a);
                              const detail = roadmapDetail(a);
                              const owner  = roadmapOwner(a);
                              const metric = roadmapMetric(a);
                              const hasMore = !!(detail || owner || metric);
                              const prog = roadmapProgress[title];
                              const isDone = prog?.status === 'done';
                              const isBuilding = prog?.status === 'building';
                              const isExpanded = !!expandedRoadmapItems[`${r.phaseKey}-${i}`];
                              return (
                                <div key={i} style={{ padding:'8px 0', borderBottom: i < phaseItems.length-1 ? '1px solid var(--border)' : 'none', opacity: isDone ? 0.6 : 1 }}>
                                <div style={{ display:'flex', alignItems:'flex-start', gap:8 }}>
                                  {/* Status toggle */}
                                  <button
                                    title={isDone ? 'Click to mark as pending' : 'Click to mark as done'}
                                    onClick={() => markRoadmapItem(title, isDone ? 'pending' : 'done')}
                                    style={{ flexShrink:0, width:18, height:18, borderRadius:'50%', border:`2px solid ${isDone?'var(--green)':isBuilding?r.color:'var(--border2)'}`, background: isDone?'var(--green)':isBuilding?`${r.color}20`:'transparent', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', marginTop:2, transition:'all 0.15s' }}>
                                    {isDone && <span style={{fontSize:10,color:'#fff',fontWeight:700,lineHeight:1}}>✓</span>}
                                    {isBuilding && <span style={{fontSize:8,color:r.color}}>●</span>}
                                  </button>
                                  {/* Title — short, scannable. Detail/owner/metric tucked behind an expand toggle. */}
                                  <span
                                    onClick={() => hasMore && setExpandedRoadmapItems(prev => ({ ...prev, [`${r.phaseKey}-${i}`]: !prev[`${r.phaseKey}-${i}`] }))}
                                    style={{ fontSize:13, lineHeight:1.5, flex:1, fontWeight:600, color: isDone?'var(--text3)':'var(--text)', textDecoration: isDone?'line-through':'', cursor: hasMore ? 'pointer' : 'default' }}>
                                    {title}
                                    {hasMore && <span style={{ marginLeft:6, fontSize:10, color:'var(--text-4)', fontWeight:400 }}>{isExpanded ? '▲' : '▼ details'}</span>}
                                  </span>
                                  {/* Build this / status */}
                                  {isDone ? (
                                    <span style={{ fontSize:10, color:'var(--green)', fontFamily:'var(--ff-m)', flexShrink:0 }}>✓ Built</span>
                                  ) : (
                                    <button
                                      className="btn btn-ghost btn-sm"
                                      style={{ fontSize:10, padding:'3px 9px', flexShrink:0, borderColor:`${r.color}50`, color: isBuilding?r.color:'var(--text3)', whiteSpace:'nowrap', background: isBuilding?`${r.color}10`:'' }}
                                      onClick={async () => {
                                        if (prog) await authFetch(`/api/roadmap/${report.company_id}/${prog.id}`, { method:'PUT', body:JSON.stringify({ status:'building', built_in_chat:1 }) }).then(()=>loadRoadmapProgress(report.company_id));
                                        // The user only sees the short title, but the AI gets the full evidence
                                        // (detail/owner/metric) so the built artifact is just as well-informed.
                                        const evidenceLine = detail ? `\n\nEvidence: ${detail}` : '';
                                        const ownerLine = owner ? `\nSuggested owner: ${owner}` : '';
                                        const metricLine = metric ? `\nSuccess metric: ${metric}` : '';
                                        navigate('/ai', {
                                          state: {
                                            companyId: report.company_id,
                                            companyName: report.company_name,
                                            _ts: Date.now(),
                                            autoPrompt: `Build this roadmap action for ${report.company_name}: "${title}"${evidenceLine}${ownerLine}${metricLine}\n\nContext: This is a ${r.label} priority (${r.phase}) from our Org DNA diagnostic. Our overall score is ${report.org_score}/5 (${report.maturity?.label}).${isAI ? ' This action was generated by AI based on the specific patterns in our assessment responses.' : ''}\n\nDeliver: A complete, immediately usable artifact — step-by-step plan with owners, timeline, templates, and success metrics. No placeholders.`
                                          }
                                        });
                                      }}>
                                      {isBuilding ? '⏳ Building…' : '✦ Build this'}
                                    </button>
                                  )}
                                </div>
                                {/* Expandable detail/owner/metric — collapsed by default to keep the list scannable */}
                                {hasMore && isExpanded && (
                                  <div style={{ marginLeft:26, marginTop:6, padding:'8px 10px', background:'var(--bg-inset)', borderRadius:8, fontSize:12, color:'var(--text-3)', lineHeight:1.6 }}>
                                    {detail && <div>{detail}</div>}
                                    {(owner || metric) && (
                                      <div style={{ marginTop:4, display:'flex', gap:14, flexWrap:'wrap' }}>
                                        {owner && <span><strong style={{color:'var(--text-2)'}}>Owner:</strong> {owner}</span>}
                                        {metric && <span><strong style={{color:'var(--text-2)'}}>Metric:</strong> {metric}</span>}
                                      </div>
                                    )}
                                  </div>
                                )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </>
          )}

          {/* Qualitative Analysis — embedded, shows when open-ended responses exist */}
          {report.open_ended?.length > 0 && (
            <QualitativeSection
              companyId={report.company_id}
              roundId={selRound}
              respondents={report.open_ended}
              isPro={report.is_pro}
              authFetch={authFetch}
              navigate={navigate}
              showToast={showToast}
            />
          )}

          {/* CTA */}
          <div className="card card-accent" style={{ textAlign: 'center', padding: 36 }}>
            <div style={{ fontFamily: 'var(--ff-d)', fontSize: 18, fontWeight: 800, marginBottom: 8 }}>Take Action with AI Collaborator</div>
            <div style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 18 }}>The AI knows your exact scores and can build RACI matrices, SOPs, KPI frameworks, and review templates specific to your findings.</div>
            <button className="btn btn-primary" style={{ padding: '11px 28px' }} onClick={() => navigate('/ai', { state: { companyId: report.company_id, companyName: report.company_name } })}>
              Open AI Collaborator →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Embedded Qualitative Analysis Section ──────────────────────────────────
   Lives inside Report — auto-shows when open-ended responses exist.
   Lets users see raw responses AND run AI theme extraction, all in one place.
──────────────────────────────────────────────────────────────────────────────*/
function QualitativeSection({ companyId, roundId, respondents, isPro, authFetch, navigate, showToast }) {
  const [view, setView] = useState('responses'); // 'responses' | 'analysis'
  const [analyzing, setAnalyzing] = useState(false);
  const [themes, setThemes] = useState(null);
  const [totalResponses, setTotalResponses] = useState(0);

  const SEV_STYLE = {
    critical: { color:'var(--red)',    bg:'rgba(239,68,68,0.06)',    border:'rgba(239,68,68,0.2)',    icon:'🔴' },
    moderate: { color:'var(--orange)', bg:'rgba(249,115,22,0.06)',   border:'rgba(249,115,22,0.2)',   icon:'🟡' },
    minor:    { color:'var(--text2)',  bg:'var(--bg-elevated)',  border:'var(--border)',           icon:'🔵' },
  };

  const runAnalysis = async () => {
    if (!isPro) { navigate('/billing'); return; }
    setAnalyzing(true);
    const body = { company_id: companyId };
    if (roundId) body.round_id = roundId;
    try {
      const res = await authFetch('/api/ai/analyze-qualitative', { method: 'POST', body: JSON.stringify(body) });
      const d = await res.json();
      if (res.ok) {
        setThemes(d.themes || []);
        setTotalResponses(d.total_responses || respondents.length);
        setView('analysis');
      } else if (d.upgrade) {
        navigate('/billing');
      } else if (d.error?.includes('ANTHROPIC_API_KEY')) {
        showToast('Add ANTHROPIC_API_KEY to server/.env to use AI analysis', 'error');
      } else {
        showToast(d.error || 'Analysis failed', 'error');
      }
    } catch { showToast('Connection error', 'error'); }
    setAnalyzing(false);
  };

  return (
    <div style={{ marginBottom: 24 }}>
      {/* Section header with tab switcher */}
      <div className="sec-header" style={{ marginBottom: 0 }}>
        <div className="sec-title">Open-Ended Responses</div>
        <div className="sec-line"></div>
        <span className="tag">{respondents.length} respondents</span>

        {/* Tab switcher */}
        <div style={{ display:'flex', gap:4, marginLeft:'auto' }}>
          <button
            className="btn btn-ghost btn-sm"
            style={{ fontSize:11, background: view==='responses'?'rgba(255,255,255,0.07)':'', borderColor: view==='responses'?'var(--border2)':'transparent' }}
            onClick={() => setView('responses')}>
            📋 Responses
          </button>
          <button
            className="btn btn-ghost btn-sm"
            style={{ fontSize:11, background: view==='analysis'?'var(--blue-soft)':'', borderColor: view==='analysis'?'var(--blue-border)':'transparent', color: view==='analysis'?'var(--accent)':'' }}
            onClick={() => themes ? setView('analysis') : runAnalysis()}>
            {analyzing ? '🧠 Analysing…' : '🧠 AI Theme Analysis'}
            {!isPro && <span style={{fontSize:9,marginLeft:4,opacity:0.7}}>PRO</span>}
          </button>
        </div>
      </div>

      {/* Divider */}
      <div style={{ height:1, background:'var(--border)', margin:'10px 0 16px' }} />

      {/* Raw responses view */}
      {view === 'responses' && (
        <div>
          {respondents.map((r, i) => (
            <div key={i} className="card" style={{ marginBottom: 10, padding: '14px 18px' }}>
              <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:10 }}>
                <span style={{ fontWeight:600, fontSize:13 }}>{r.respondent}</span>
                {r.department && <span className="tag" style={{fontSize:11}}>{r.department}</span>}
              </div>
              {r.responses.map((resp, j) => (
                <div key={j} style={{ marginBottom: 10, paddingLeft: 12, borderLeft: '2px solid var(--border)' }}>
                  <div style={{ fontSize:10, color:'var(--text3)', marginBottom:3, fontFamily:'var(--ff-m)', textTransform:'uppercase', letterSpacing:0.5 }}>{resp.question}</div>
                  <div style={{ fontSize:13, color:'var(--text2)', lineHeight:1.6, fontStyle:'italic' }}>"{resp.answer}"</div>
                </div>
              ))}
            </div>
          ))}

          {/* Prompt to run analysis */}
          <div style={{ padding:'14px 18px', background:'rgba(201,151,58,0.04)', borderRadius:'var(--r)', border:'1px solid var(--blue-border)', display:'flex', justifyContent:'space-between', alignItems:'center', gap:16, flexWrap:'wrap' }}>
            <div>
              <div style={{ fontWeight:600, fontSize:13, marginBottom:3 }}>🧠 Extract AI themes from these responses</div>
              <div style={{ fontSize:12, color:'var(--text2)' }}>AI identifies patterns, severity, and exact quotes across all {respondents.length} open-ended answers</div>
            </div>
            <button
              className="btn btn-primary btn-sm"
              style={{ whiteSpace:'nowrap' }}
              disabled={analyzing}
              onClick={runAnalysis}>
              {analyzing ? 'Analysing…' : isPro ? 'Run Analysis →' : '🔒 Upgrade to Run'}
            </button>
          </div>
        </div>
      )}

      {/* AI theme analysis view */}
      {view === 'analysis' && analyzing && (
        <div className="card" style={{ textAlign:'center', padding:40 }}>
          <div className="loader" style={{ margin:'0 auto 16px' }}></div>
          <div style={{ fontSize:14, color:'var(--text2)' }}>Reading all responses and extracting themes…</div>
          <div style={{ fontSize:12, color:'var(--text3)', marginTop:6 }}>This takes 10–20 seconds</div>
        </div>
      )}

      {view === 'analysis' && themes && !analyzing && (
        <div className="fade-in">
          <div style={{ padding:'10px 14px', background:'rgba(201,151,58,0.04)', borderRadius:8, border:'1px solid var(--blue-border)', marginBottom:16, display:'flex', justifyContent:'space-between', alignItems:'center', gap:12 }}>
            <div style={{ fontSize:12, color:'var(--text2)' }}>
              🧠 <strong style={{color:'var(--blue)'}}>{totalResponses} responses</strong> analysed · {themes.length} themes identified
            </div>
            <button className="btn btn-ghost btn-sm" style={{fontSize:11}} onClick={() => { setThemes(null); runAnalysis(); }}>
              Re-analyse
            </button>
          </div>

          {themes.map((theme, i) => {
            const sev = SEV_STYLE[theme.severity] || SEV_STYLE.minor;
            return (
              <div key={i} className="card fade-in" style={{ marginBottom:12, borderColor:sev.border, background:sev.bg }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10, flexWrap:'wrap', gap:8 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <span style={{ fontSize:18 }}>{sev.icon}</span>
                    <span style={{ fontFamily:'var(--ff-d)', fontSize:15, fontWeight:800, color:sev.color }}>{theme.title}</span>
                  </div>
                  <div style={{ display:'flex', gap:7 }}>
                    <span className="tag" style={{ fontSize:10, color:sev.color, borderColor:`${sev.color}40`, background:`${sev.color}10` }}>
                      {theme.severity?.toUpperCase()}
                    </span>
                    <span className="tag" style={{ fontSize:10 }}>
                      {theme.frequency} mention{theme.frequency!==1?'s':''}
                    </span>
                  </div>
                </div>
                {theme.implication && (
                  <div style={{ fontSize:13, color:'var(--text2)', marginBottom:10, lineHeight:1.6, paddingLeft:28 }}>
                    {theme.implication}
                  </div>
                )}
                {theme.quotes?.length > 0 && (
                  <div style={{ paddingLeft:28 }}>
                    <div style={{ fontSize:10, color:'var(--text3)', fontFamily:'var(--ff-m)', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>
                      Verbatim quotes
                    </div>
                    {theme.quotes.map((q,j) => (
                      <div key={j} style={{ padding:'7px 12px', background:'rgba(0,0,0,0.2)', borderRadius:6, fontSize:12, color:'var(--text2)', fontStyle:'italic', marginBottom:6, borderLeft:`2px solid ${sev.color}40` }}>
                        "{q}"
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          <button className="btn btn-ghost btn-sm" style={{fontSize:11}} onClick={()=>setView('responses')}>
            ← View raw responses
          </button>
        </div>
      )}
    </div>
  );
}
